/**
 * Per-tick input frames. Inputs are quantized *before* the simulation sees
 * them, so a lap replayed from its recording feeds the simulation exactly the
 * same values it saw live. A frame packs into 3 bytes; recordings are
 * run-length encoded, which makes keyboard laps tiny.
 */

export interface InputFrame {
  /** 0..255 */
  throttle: number;
  /** 0..255 */
  brake: number;
  /** -127..127, positive = left */
  steer: number;
}

export const NEUTRAL_INPUT: Readonly<InputFrame> = Object.freeze({ throttle: 0, brake: 0, steer: 0 });

export function quantizeInput(throttle: number, brake: number, steer: number): InputFrame {
  return {
    throttle: Math.round(clamp01(throttle) * 255),
    brake: Math.round(clamp01(brake) * 255),
    steer: Math.round(Math.max(-1, Math.min(1, steer)) * 127),
  };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function packInput(f: InputFrame): number {
  return (f.throttle & 0xff) | ((f.brake & 0xff) << 8) | (((f.steer + 128) & 0xff) << 16);
}

export function unpackInput(v: number): InputFrame {
  return { throttle: v & 0xff, brake: (v >>> 8) & 0xff, steer: ((v >>> 16) & 0xff) - 128 };
}

const MAGIC = 0x47; // 'G'
const FORMAT = 1;

/** Run-length encode packed frames into a compact base64 string. */
export function encodeInputs(packed: ArrayLike<number>): string {
  const bytes: number[] = [MAGIC, FORMAT];
  writeVarint(bytes, packed.length);
  let i = 0;
  while (i < packed.length) {
    const v = packed[i];
    let run = 1;
    while (i + run < packed.length && packed[i + run] === v) run++;
    writeVarint(bytes, run);
    bytes.push(v & 0xff, (v >>> 8) & 0xff, (v >>> 16) & 0xff);
    i += run;
  }
  return bytesToBase64(bytes);
}

/** The longest recording read back: an hour (a board lap's run is at most ten minutes). It bounds the memory a made-up one can ask for. */
const MAX_TICKS = 60 * 3600;

/**
 * A recording back into one packed frame per tick. Recordings come from other players too (the board, a
 * link), so anything that isn't a well-formed one throws rather than being trusted: a run that would never
 * end, or one longer than the recording says, would otherwise hang whoever reads it.
 */
export function decodeInputs(encoded: string): number[] {
  const bytes = base64ToBytes(encoded);
  if (bytes.length < 3 || bytes[0] !== MAGIC || bytes[1] !== FORMAT) throw new Error('Unknown input recording format');
  const pos = { i: 2 };
  const total = readVarint(bytes, pos);
  if (total > MAX_TICKS) throw new Error('Input recording too long');
  const out: number[] = [];
  while (out.length < total) {
    const run = readVarint(bytes, pos);
    if (run < 1 || out.length + run > total || pos.i + 3 > bytes.length) throw new Error('Corrupt input recording');
    const v = bytes[pos.i] | (bytes[pos.i + 1] << 8) | (bytes[pos.i + 2] << 16);
    pos.i += 3;
    for (let k = 0; k < run; k++) out.push(v);
  }
  return out;
}

function writeVarint(out: number[], v: number): void {
  let x = v >>> 0;
  while (x >= 0x80) {
    out.push((x & 0x7f) | 0x80);
    x >>>= 7;
  }
  out.push(x);
}

function readVarint(bytes: Uint8Array, pos: { i: number }): number {
  let result = 0;
  let shift = 0;
  for (;;) {
    if (pos.i >= bytes.length || shift > 28) throw new Error('Corrupt input recording');
    const b = bytes[pos.i++];
    result |= (b & 0x7f) << shift;
    if ((b & 0x80) === 0) break;
    shift += 7;
  }
  return result >>> 0;
}

function bytesToBase64(bytes: number[]): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
